import { useI18n } from "@/i18n/I18nProvider";
import { Reveal } from "@/hooks/useReveal";
import {
  ShieldCheck,
  Network,
  Cloud,
  Lock,
  BadgeCheck,
  type LucideIcon,
} from "lucide-react";

const pillars: { key: string; Icon: LucideIcon }[] = [
  { key: "secure",     Icon: ShieldCheck },
  { key: "scalable",   Icon: Network },
  { key: "cloud",      Icon: Cloud },
  { key: "privacy",    Icon: Lock },
  { key: "enterprise", Icon: BadgeCheck },
];

export function TrustSection({ index = "05" }: { index?: string }) {
  const { t } = useI18n();
  const data = t.trust;

  return (
    <section className="border-t hairline">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <Reveal className="lg:col-span-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
              — {index} / {data.eyebrow}
            </p>
            <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter">
              {data.title}
            </h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
              {data.subtitle}
            </p>
          </Reveal>
        </div>

        <div className="grid gap-px bg-border border hairline rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-5">
          {pillars.map(({ key, Icon }, i) => {
            const p = (data.pillars as Record<string, { t: string; d: string }>)[key];
            return (
              <Reveal key={key} delay={i * 60} className="bg-background">
                <div className="group h-full p-6 lg:p-7 card-lift">
                  <Icon
                    className="h-6 w-6 mb-6 text-foreground/80 transition-transform duration-500 group-hover:scale-110"
                    strokeWidth={1.4}
                  />
                  <h3 className="font-display text-base lg:text-lg font-semibold tracking-tight mb-2">
                    {p.t}
                  </h3>
                  <p className="text-xs lg:text-sm text-muted-foreground leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={200}>
          <div className="mt-10 flex flex-wrap items-center gap-2">
            {(data.badges as string[]).map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 rounded-full border hairline bg-foreground/[0.02] px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground"
              >
                <BadgeCheck className="h-3 w-3" strokeWidth={1.5} />
                {b}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}